import { mapProjectDtoToModel, ProjectDto, ProjectModel } from 'api/dto/Project.dto';
import dayjs from 'dayjs';

export type PulseTemplateDto = {
  hasChildProjectsAnyPublishableSubmits: boolean;
  project: ProjectDto;
  pulseProjects: ProjectDto[];
  pulseOnDemandProjects: ProjectDto[];
  pulseLifecycleProjects: ProjectDto[];
};

export type PulseSubProjectDto = {
  name: ProjectDto['name'];
  updatedAt: ProjectDto['updatedAt'];
  createdBy: ProjectDto['createdBy'];
  createdAt: ProjectDto['createdAt'];
  status: ProjectDto['status'];
  kind: ProjectDto['kind'];
  liveStart: ProjectDto['liveStart'];
  liveStop: ProjectDto['liveStop'];
  responseRate: ProjectDto['responseRate'];
  id: ProjectDto['id'];
  templateId: ProjectDto['id'];
};

export type PulseTemplateModel = {
  hasChildProjectsAnyPublishableSubmits: boolean;
  project: ProjectModel;
  pulseProjects: ProjectModel[];
  pulseOnDemandPolls: ProjectModel[];
  pulseLifecycleProjects: ProjectModel[];
};

export function mapPulseTemplateDtoToModel(dto: PulseTemplateDto): PulseTemplateModel {
  const {
    project,
    pulseProjects = [],
    pulseOnDemandProjects = [],
    pulseLifecycleProjects = [],
  } = dto;

  return {
    hasChildProjectsAnyPublishableSubmits: dto.hasChildProjectsAnyPublishableSubmits,
    project: mapProjectDtoToModel(project),
    pulseProjects: pulseProjects.map((pulse) => mapProjectDtoToModel(pulse)),
    pulseOnDemandPolls: pulseOnDemandProjects.map((OnDemand) => mapProjectDtoToModel(OnDemand)),
    pulseLifecycleProjects: pulseLifecycleProjects.map((lifecycle) =>
      mapProjectDtoToModel(lifecycle),
    ),
  };
}

export function mapPulseSubProjectDtoToModel(dto: PulseSubProjectDto): ProjectModel {
  const formattedLiveStart = dto.liveStart && dto.liveStart.split('T')[0];
  const formattedLiveStop = dto.liveStop && dto.liveStop.split('T')[0];

  return {
    name: dto.name,
    updatedAt: dayjs.utc(dto.updatedAt),
    createdAt: dto.createdAt && dayjs.utc(dto.createdAt),
    status: dto.status,
    kind: dto.kind,
    liveStart: dayjs(formattedLiveStart),
    liveStop: dayjs(formattedLiveStop),
    responseRate: dto.responseRate,
    id: dto.id,
    // TODO separate SubProject and Project class. Sub project should not use full Project model
  } as ProjectModel;
}

export const defaultPulseFrequencyId = 'quarterly';

export namespace PulseTemplateModel {
  export function isPulseTemplateModel(value: any): value is PulseTemplateModel {
    return (
      value &&
      typeof value === 'object' &&
      'project' in value &&
      'pulseProjects' in value &&
      'pulseOnDemandPolls' in value &&
      'pulseLifecycleProjects' in value
    );
  }

  export const getChildProjects = (template: PulseTemplateModel): ProjectModel[] => [
    ...template.pulseProjects,
    ...template.pulseOnDemandPolls,
  ];

  export const getLiveAndScheduledSurveys = (template: PulseTemplateModel): ProjectModel[] =>
    template.pulseProjects.filter(
      (project) => ProjectModel.isLive(project) || ProjectModel.isInDevelopment(project),
    );

  export const getArchivedSurveys = (template: PulseTemplateModel): ProjectModel[] =>
    template.pulseProjects
      .filter((project) => ProjectModel.isClosed(project))
      .sort(ProjectModel.sortByLiveStop);

  export const getLiveAndScheduledDemandPolls = (template: PulseTemplateModel): ProjectModel[] =>
    template.pulseOnDemandPolls.filter(
      (project) => ProjectModel.isLive(project) || ProjectModel.isInDevelopment(project),
    );

  export const getArchivedDemandPolls = (template: PulseTemplateModel): ProjectModel[] =>
    template.pulseOnDemandPolls
      .filter((project) => ProjectModel.isClosed(project))
      .sort(ProjectModel.sortByLiveStop);
}
